/* eslint-disable max-len */
import { BasicFilterApiModel } from '../../../api/shared/models/basic-filter.api.model';
import { isEqual, sortBy } from 'underscore';
import { IncludeOrExclude } from '../../../Common/Dialogs/services/data-select-dialog.service.interface';

/**
 * Constructs a type containing all IncludeExclude model properties on a given object O.
 * Uses conditional types to determine whether a given property is an IncludeExcludeModel or not.
 */
export type IncludeExcludeKeys<O> = {
    [k in keyof O]: O[k] extends IncludeExcludeModel<any> ? k : never
}[keyof O];

export class IncludeExcludeModel<T> {
    includeAll?: T[];
    include: T[];
    exclude: T[];

    constructor(apiModel?: BasicFilterApiModel<T>) {
        if (apiModel) {
            if (apiModel.includeAll) {
                this.includeAll = [...apiModel.includeAll];
            }

            this.include = [...apiModel.include];
            this.exclude = [...apiModel.exclude];
        } else {
            this.includeAll = [];
            this.include = [];
            this.exclude = [];
        }
    }

    static fromArray(include: string[]): IncludeExcludeModel<string> {
        const val = new IncludeExcludeModel<string>();
        val.include = include;
        return val;
    }

    // for another equivalently-typed include exclude model, merge its unique values into ours
    merge(other: IncludeExcludeModel<T>, properties: IncludeOrExclude[] = [ IncludeOrExclude.includeAll, IncludeOrExclude.include, IncludeOrExclude.exclude ]): void {
        for (let i = 0; i < properties.length; i++) {
            const property = properties[i];

            this[property].push(...other[property]);

            // de-duplicate
            this[property] = Array.from(new Set(this[property]));
        }
    }

    toSubModel(property: keyof T): IncludeExcludeModel<IncludeExcludeKeys<T>> {
        return new IncludeExcludeModel<any>(
            {
                includeAll: this.includeAll?.map(value => value[property]),
                include: this.include.map(value => value[property]),
                exclude: this.exclude.map(value => value[property])
            }
        );
    }

    getAllChildren(property: keyof T): IncludeExcludeModel<IncludeExcludeKeys<T>> {
        const allChildren = new IncludeExcludeModel({
            includeAll: [],
            include: [],
            exclude: []
        });

        this.includeAll?.forEach(parent => parent['children'].forEach(child => allChildren.includeAll.push(child[property])));
        this.include?.forEach(parent => parent['children'].forEach(child => allChildren.include.push(child[property])));
        this.exclude?.forEach(parent => parent['children'].forEach(child => allChildren.exclude.push(child[property])));

        return allChildren;
    }

    toApiModel(): BasicFilterApiModel<T> {
        return <BasicFilterApiModel<T>>{
            includeAll: this.includeAll,
            include: this.include,
            exclude: this.exclude
        };
    }

    clone(): IncludeExcludeModel<T> {
        return new IncludeExcludeModel<T>(this.toApiModel());
    }

    equals(other?: IncludeExcludeModel<T>) {
        if (!other) {
            return false;
        }

        return isEqual(sortBy(this.includeAll), sortBy(other.includeAll)) &&
            isEqual(sortBy(this.include), sortBy(other.include)) &&
            isEqual(sortBy(this.exclude), sortBy(other.exclude));
    }

    isEmpty() {
        // Because includeAll is optional, we don't want it to affect the result if it's undefined
        return this.exclude.length === 0 && this.include.length === 0 && (this.includeAll ? this.includeAll.length === 0 : true);
    }
}
